import { ThemeEvents, SlideshowSelectEvent } from '@theme/events';

const EVENT_NAMESPACE = 'melden_jewels';
const LABEL_MAX_LENGTH = 80;
const RECENT_EVENT_TTL_MS = 1200;
const SEARCH_IDLE_MS = 1200;
const SCROLL_DEPTHS = [25, 50, 75, 90];
const PAGE_ENGAGEMENT_SECONDS = [15, 45];

const recentEvents = new Map();
const emittedScrollDepths = new Set();
const emittedEngagementSeconds = new Set();
const startedForms = new WeakSet();
const debugEnabled = new URLSearchParams(window.location.search).has('mj_analytics_debug');
let searchInputTimer = null;

registerGlobalListeners();
registerEngagementListeners();

function registerGlobalListeners() {
  document.addEventListener('click', handleGlobalClick);
  document.addEventListener('change', handleGlobalChange, true);
  document.addEventListener('input', handleGlobalInput, true);
  document.addEventListener('focusin', handleGlobalFocus, true);
  document.addEventListener('submit', handleGlobalSubmit, true);
  document.addEventListener('toggle', handleDetailsToggle, true);
  document.addEventListener('dialog:open', handleDialogOpen, true);
  document.addEventListener('dialog:close', handleDialogClose, true);
  document.addEventListener(ThemeEvents.FilterUpdate, handleFilterUpdate);
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
  document.addEventListener(ThemeEvents.cartError, handleCartError);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, handleQuantitySelectorUpdate);
  document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
  document.addEventListener(ThemeEvents.variantSelected, handleVariantSelected);
  document.addEventListener(ThemeEvents.zoomMediaSelected, handleZoomMediaSelected);
  document.addEventListener(SlideshowSelectEvent.eventName, handleSlideshowSelect);
}

function registerEngagementListeners() {
  document.addEventListener('scroll', throttle(handleScrollDepth, 500), { passive: true });

  PAGE_ENGAGEMENT_SECONDS.forEach((seconds) => {
    window.setTimeout(() => {
      if (document.visibilityState !== 'visible' || emittedEngagementSeconds.has(seconds)) return;

      emittedEngagementSeconds.add(seconds);
      publish('page_engaged', {
        engaged_seconds: seconds,
        source: 'theme_timer',
      });
    }, seconds * 1000);
  });
}

function handleGlobalClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const searchResult = target.closest('.predictive-search a');
  if (searchResult instanceof HTMLAnchorElement) {
    publishSearchResultClick(searchResult);
    return;
  }

  const checkoutButton = target.closest('button[name="checkout"], .cart__checkout-button');
  if (checkoutButton) {
    publishDeduped(
      'checkout_intent',
      {
        cart_location: getCartLocation(checkoutButton),
        item_count: getCartItemCount(),
        source: 'theme_checkout_button',
      },
      `checkout:${getCartLocation(checkoutButton)}`,
    );
    return;
  }

  const searchButton = target.closest('search-button, .search-action, [aria-controls="search-modal"]');
  if (searchButton) {
    publishDeduped(
      'search_opened',
      {
        source: 'theme_search_button',
        surface: getSurface(searchButton),
      },
      `search_opened:${getSurface(searchButton)}`,
    );
    return;
  }

  const cartTrigger = target.closest('[data-testid="cart-drawer-trigger"], .action__cart, cart-icon');
  if (cartTrigger) {
    publishDeduped(
      'cart_opened',
      {
        item_count: getCartItemCount(),
        source: 'theme_cart_trigger',
        surface: getSurface(cartTrigger),
      },
      `cart_opened:${getSurface(cartTrigger)}`,
    );
    return;
  }

  const productCard = target.closest('product-card, [data-product-id].product-grid__item');
  if (productCard) {
    const cardLink = getClosestLink(target) || productCard.querySelector?.('a[href]');
    publishDeduped(
      'product_card_clicked',
      {
        product_id: safeString(productCard.dataset?.productId || productCard.querySelector?.('[data-product-id]')?.dataset?.productId),
        product_handle: getHandleFromHref(cardLink?.href, 'products'),
        list_context: getListContext(productCard),
        card_index: getCardIndex(productCard),
        link_path: getPathFromHref(cardLink?.href),
        source: 'theme_product_card',
      },
      `product_card:${safeString(productCard.dataset?.productId)}:${getPathFromHref(cardLink?.href)}`,
    );
    return;
  }

  const filterButton = target.closest('.facets__filter-sort-trigger, [aria-haspopup="dialog"]');
  const opensFilterDrawer = filterButton?.classList.contains('facets__filter-sort-trigger')
    || safeString(filterButton?.getAttribute('on:click')).includes('filters-drawer')
    || safeString(filterButton?.getAttribute('aria-controls')).includes('filters-drawer');

  if (filterButton && opensFilterDrawer) {
    publishDeduped(
      'collection_filter_drawer_opened',
      {
        active_filter_count: countActiveFilters(),
        source: 'theme_filter_button',
      },
      'filter_drawer_opened',
    );
    return;
  }

  const trustInteractive = target.closest('a, button, summary, [role="button"]');
  if (trustInteractive && isProductPage()) {
    const label = sanitizeLabel(trustInteractive.textContent || trustInteractive.getAttribute('aria-label'));
    if (isTrustLabel(label)) {
      publishDeduped(
        'pdp_trust_interaction',
        {
          interaction_label: label,
          interaction_type: trustInteractive.tagName.toLowerCase(),
          source: 'theme_trust_interaction',
        },
        `trust_interaction:${trustInteractive.tagName}:${label}`,
      );
      return;
    }
  }

  const link = getClosestLink(target);
  if (link) {
    publishLinkClick(link, target);
    return;
  }

  const cta = target.closest('button, [role="button"], .button');
  if (cta && !isFormControlButton(cta)) {
    publishDeduped(
      'cta_clicked',
      {
        cta_label: sanitizeLabel(cta.textContent || cta.getAttribute('aria-label')),
        surface: getSurface(cta),
        source: 'theme_button_click',
      },
      `cta:${getSurface(cta)}:${sanitizeLabel(cta.textContent || cta.getAttribute('aria-label'))}`,
    );
  }
}

function handleGlobalChange(event) {
  const target = event.target;

  if (target instanceof HTMLInputElement && target.name === 'sort_by') {
    publishSortSelected(target.value, target.dataset.optionName);
    return;
  }

  if (target instanceof HTMLSelectElement && target.name === 'sort_by') {
    publishSortSelected(target.value, target.dataset.optionName || target.selectedOptions[0]?.textContent);
    return;
  }

  if (target instanceof HTMLInputElement && target.name?.startsWith('filter.')) {
    publishDeduped(
      'collection_filter_selected',
      {
        filter_name: safeFilterName(target.name),
        selected: target.checked,
        active_filter_count: countActiveFilters(),
        source: 'theme_filter_input',
      },
      `filter_input:${target.name}:${target.value}:${target.checked}`,
    );
    return;
  }

  if (target instanceof HTMLSelectElement && target.closest('localization-form-component')) {
    publishDeduped(
      'localization_changed',
      {
        language: target.name?.includes('locale') ? safeString(target.value) : '',
        source: 'theme_language_select',
      },
      `localization_select:${target.name}:${target.value}`,
    );
  }
}

function handleGlobalInput(event) {
  const target = event.target;

  if (target instanceof HTMLInputElement && target.type === 'search') {
    const queryLength = target.value.trim().length;
    if (searchInputTimer) window.clearTimeout(searchInputTimer);
    if (queryLength < 2) return;

    searchInputTimer = window.setTimeout(() => {
      publishDeduped(
        'search_typed',
        {
          query_length: queryLength,
          surface: getSurface(target),
          source: 'theme_search_input',
        },
        `search_typed:${queryLength}:${getSurface(target)}`,
      );
    }, SEARCH_IDLE_MS);
    return;
  }

  if (target instanceof HTMLTextAreaElement && target.id === 'cart-note') {
    publishDeduped(
      'cart_note_started',
      {
        cart_location: getCartLocation(target),
        source: 'theme_cart_note',
      },
      `cart_note:${getCartLocation(target)}`,
    );
  }
}

function handleGlobalFocus(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const form = target.closest('form');
  if (!form || startedForms.has(form)) return;

  const formType = classifyForm(form);
  if (!formType) return;

  startedForms.add(form);
  publishDeduped(
    'form_started',
    {
      form_type: formType,
      source: 'theme_form_focus',
    },
    `form_started:${formType}:${form.id || getSurface(form)}`,
  );
}

function handleGlobalSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const formType = classifyForm(form);

  if (formType === 'search') {
    const queryLength = safeString(new FormData(form).get('q')).length;
    publishDeduped(
      'search_submitted',
      {
        query_length: queryLength,
        surface: getSurface(form),
        source: 'theme_search_submit',
      },
      `search_submit:${queryLength}:${getSurface(form)}`,
    );
    return;
  }

  if (formType === 'discount') {
    publishDeduped(
      'discount_submitted',
      {
        cart_location: getCartLocation(form),
        source: 'theme_discount_form',
      },
      `discount_submit:${getCartLocation(form)}`,
    );
    return;
  }

  if (formType === 'localization') {
    publishDeduped(
      'localization_submitted',
      {
        source: 'theme_localization_form',
      },
      `localization_submit:${getSurface(form)}`,
    );
    return;
  }

  if (formType) {
    publishDeduped(
      'form_submitted',
      {
        form_type: formType,
        source: 'theme_form_submit',
      },
      `form_submit:${formType}:${form.id || getSurface(form)}`,
    );
  }
}

function handleDialogOpen(event) {
  const component = event.target instanceof Element ? event.target : null;
  const dialogType = classifyDialog(component);
  if (!dialogType) return;

  publishDeduped(
    `${dialogType}_opened`,
    {
      item_count: dialogType === 'cart' ? getCartItemCount() : undefined,
      active_filter_count: dialogType === 'collection_filter_drawer' ? countActiveFilters() : undefined,
      source: 'theme_dialog_open',
    },
    `dialog_open:${dialogType}`,
  );
}

function handleDialogClose(event) {
  const component = event.target instanceof Element ? event.target : null;
  const dialogType = classifyDialog(component);
  if (!dialogType) return;

  publishDeduped(
    `${dialogType}_closed`,
    {
      item_count: dialogType === 'cart' ? getCartItemCount() : undefined,
      source: 'theme_dialog_close',
    },
    `dialog_close:${dialogType}`,
  );
}

function handleFilterUpdate(event) {
  publishDeduped(
    'collection_filters_updated',
    {
      active_filter_count: countFilterParams(event.detail?.queryParams),
      has_search_query: Boolean(event.detail?.queryParams?.get?.('q')),
      sort_by: safeString(event.detail?.queryParams?.get?.('sort_by')),
      source: 'theme_filter_update',
    },
    `filter_update:${event.detail?.queryParams?.toString?.() || window.location.search}`,
  );
}

function handleCartUpdate(event) {
  const source = safeString(event.detail?.data?.source || event.detail?.sourceId);
  const isAdd = source.includes('product-form') || source.includes('quick-add');

  publishDeduped(
    isAdd ? 'cart_add_completed' : 'cart_updated',
    {
      item_count: event.detail?.data?.itemCount || event.detail?.resource?.item_count || getCartItemCount(),
      product_id: safeString(event.detail?.data?.productId),
      variant_id: safeString(event.detail?.data?.variantId || event.detail?.sourceId),
      update_source: source,
      cart_location: getCartLocation(event.target),
      source: 'theme_cart_event',
    },
    `cart_update:${source}:${safeString(event.detail?.data?.productId)}:${event.detail?.data?.itemCount || ''}`,
  );
}

function handleCartError(event) {
  publishDeduped(
    isProductPage() ? 'pdp_add_to_cart_error' : 'cart_error',
    {
      source: safeString(event.detail?.sourceId || event.detail?.data?.source),
      error_type: classifyCartError(event.detail?.data),
      cart_location: getCartLocation(event.target),
    },
    `cart_error:${getCurrentVariantId()}:${classifyCartError(event.detail?.data)}`,
  );
}

function handleQuantitySelectorUpdate(event) {
  const target = event.target instanceof Element ? event.target : null;
  const cartContext = target?.closest?.('cart-items-component, .cart-page, .cart-drawer');
  if (!cartContext) return;

  publishDeduped(
    'cart_quantity_changed',
    {
      quantity: typeof event.detail?.quantity === 'number' ? event.detail.quantity : null,
      cart_line: typeof event.detail?.cartLine === 'number' ? event.detail.cartLine : null,
      cart_location: getCartLocation(target),
      source: 'theme_quantity_selector',
    },
    `cart_quantity:${event.detail?.cartLine}:${event.detail?.quantity}:${getCartLocation(target)}`,
  );
}

function handleVariantUpdate(event) {
  if (!isProductPageEvent(event)) return;

  const variant = event.detail?.resource || {};
  const payload = {
    variant_id: safeString(variant.id || getCurrentVariantId()),
    variant_available: typeof variant.available === 'boolean' ? variant.available : null,
    variant_sku_present: Boolean(variant.sku),
    source: 'theme_variant_update',
  };

  publishDeduped('pdp_variant_selected', payload, `variant_update:${payload.variant_id}`);
}

function handleVariantSelected(event) {
  if (!isProductPageEvent(event)) return;

  const selectedInput = event.target?.querySelector?.('input:checked[data-option-value-id], input[data-current-checked="true"]');
  const optionName = getOptionName(selectedInput);
  const optionValue = safeString(selectedInput?.dataset?.optionLabel || selectedInput?.value);
  const variantId = safeString(selectedInput?.dataset?.variantId || getCurrentVariantId());

  publishDeduped(
    'pdp_option_selected',
    {
      variant_id: variantId,
      option_name: optionName,
      option_value: optionValue,
      option_value_id: safeString(event.detail?.resource?.id || selectedInput?.dataset?.optionValueId),
      option_available: parseBoolean(selectedInput?.dataset?.optionAvailable),
      source: 'theme_variant_selected',
    },
    `option_selected:${optionName}:${optionValue}:${variantId}`,
  );
}

function handleZoomMediaSelected(event) {
  if (!isProductPageEvent(event)) return;

  publishDeduped(
    'pdp_media_zoomed',
    {
      media_index: typeof event.detail?.index === 'number' ? event.detail.index : null,
      source: 'theme_media_zoom',
    },
    `media_zoom:${event.detail?.index ?? 'unknown'}`,
  );
}

function handleSlideshowSelect(event) {
  if (!isProductPageEvent(event) || !event.detail?.userInitiated) return;

  publishDeduped(
    'pdp_media_selected',
    {
      media_index: typeof event.detail.index === 'number' ? event.detail.index : null,
      media_id_present: Boolean(event.detail.id),
      trigger: safeString(event.detail.trigger),
      source: 'theme_media_gallery',
    },
    `media_select:${event.detail.index}:${event.detail.trigger}`,
  );
}

function handleDetailsToggle(event) {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement) || !details.open) return;

  const label = sanitizeLabel(details.querySelector('summary')?.textContent);
  if (!label) return;

  if (isProductPage() && getMainContent()?.contains(details)) {
    publishDeduped(
      'pdp_details_opened',
      {
        detail_label: label,
        source: 'theme_details_toggle',
      },
      `pdp_details_opened:${label}`,
    );
    return;
  }

  if (details.closest('.cart-actions, .cart-drawer, .cart-page')) {
    publishDeduped(
      'cart_details_opened',
      {
        detail_label: label,
        cart_location: getCartLocation(details),
        source: 'theme_details_toggle',
      },
      `cart_details_opened:${label}:${getCartLocation(details)}`,
    );
  }
}

function handleScrollDepth() {
  const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollableHeight <= 0) return;

  const depth = Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100));
  const threshold = SCROLL_DEPTHS.find((candidate) => depth >= candidate && !emittedScrollDepths.has(candidate));
  if (!threshold) return;

  emittedScrollDepths.add(threshold);
  publish('page_scroll_depth', {
    scroll_depth: threshold,
    source: 'theme_scroll',
  });
}

function publishLinkClick(link, target) {
  const href = link.href;
  const linkType = classifyLink(href);
  const surface = getSurface(target);
  const path = getPathFromHref(href);
  const label = sanitizeLabel(link.textContent || link.getAttribute('aria-label') || link.title);

  if (path.includes('/collections/')) {
    publishDeduped(
      'collection_link_clicked',
      {
        collection_handle: getHandleFromHref(href, 'collections'),
        link_label: label,
        surface,
        link_type: linkType,
        link_path: path,
        source: 'theme_collection_link',
      },
      `collection_link:${surface}:${path}:${label}`,
    );
    return;
  }

  if (surface === 'header') {
    publishDeduped(
      'navigation_clicked',
      {
        nav_label: label,
        link_type: linkType,
        link_path: path,
        source: 'theme_header_link',
      },
      `navigation:${path}:${label}`,
    );
    return;
  }

  if (surface === 'footer') {
    publishDeduped(
      'footer_link_clicked',
      {
        link_label: label,
        link_type: linkType,
        link_path: path,
        source: 'theme_footer_link',
      },
      `footer:${path}:${label}`,
    );
    return;
  }

  if (link.classList.contains('button') || link.closest('.button, [role="button"]')) {
    publishDeduped(
      'cta_clicked',
      {
        cta_label: label,
        surface,
        link_type: linkType,
        link_path: path,
        source: 'theme_cta_link',
      },
      `cta_link:${surface}:${path}:${label}`,
    );
  }
}

function publishSearchResultClick(link) {
  const path = getPathFromHref(link.href);
  const resultType = classifyResultPath(path);

  publishDeduped(
    'search_result_clicked',
    {
      result_type: resultType,
      product_handle: resultType === 'product' ? getHandleFromHref(link.href, 'products') : '',
      collection_handle: resultType === 'collection' ? getHandleFromHref(link.href, 'collections') : '',
      result_position: getSearchResultPosition(link),
      link_path: path,
      source: 'theme_predictive_search',
    },
    `search_result:${path}:${getSearchResultPosition(link)}`,
  );
}

function publishSortSelected(value, label) {
  publishDeduped(
    'collection_sort_selected',
    {
      sort_by: safeString(value),
      sort_label: sanitizeLabel(label),
      source: 'theme_sort_input',
    },
    `sort:${value}`,
  );
}

function publishDeduped(eventName, payload, dedupeKey) {
  const key = `${eventName}:${dedupeKey}`;
  const now = Date.now();
  const previous = recentEvents.get(key);

  if (previous && now - previous < RECENT_EVENT_TTL_MS) return;

  recentEvents.set(key, now);
  publish(eventName, payload);
}

function publish(eventName, payload) {
  const analytics = window.Shopify?.analytics;
  const eventPayload = pruneEmptyValues({
    ...basePayload(),
    ...payload,
  });

  if (!analytics || typeof analytics.publish !== 'function') {
    debug('missing_shopify_analytics', eventName, eventPayload);
    return;
  }

  analytics.publish(`${EVENT_NAMESPACE}:${eventName}`, eventPayload);
  debug('published', eventName, eventPayload);
}

function basePayload() {
  return {
    page_type: getPageType(),
    template: safeString(getMainContent()?.dataset.template),
    locale: safeString(document.documentElement.lang),
    currency: safeString(window.Shopify?.currency?.active),
    product_id: isProductPage() ? getProductId() : '',
    product_handle: isProductPage() ? getProductHandle() : '',
    variant_id: isProductPage() ? getCurrentVariantId() : '',
    viewport: getViewportCategory(),
    path: window.location.pathname,
  };
}

function classifyDialog(component) {
  if (!component) return '';
  if (component.id === 'search-modal' || component.matches?.('#search-modal')) return 'search';
  if (component.matches?.('cart-drawer-component, .cart-drawer') || component.querySelector?.('[data-testid="cart-drawer-dialog"]')) return 'cart';
  if (component.id === 'filters-drawer' || component.matches?.('#filters-drawer')) return 'collection_filter_drawer';
  if (component.classList?.contains('quick-add-modal')) return 'quick_add_modal';
  return '';
}

function classifyForm(form) {
  if (form.matches('.cart-discount__form') || form.closest('cart-discount-component')) return 'discount';
  if (form.matches('.contact-form__form') || form.querySelector('[name="contact[body]"]')) return 'contact';
  if (form.closest('localization-form-component') || form.action.includes('/localization')) return 'localization';
  if (form.getAttribute('role') === 'search' || form.querySelector('input[name="q"]')) return 'search';
  if (form.querySelector('input[type="email"]') && /newsletter|subscribe|customer/i.test(form.textContent || form.className)) return 'newsletter';
  return '';
}

function classifyLink(href) {
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return 'external';
    if (url.hash && url.pathname === window.location.pathname) return 'anchor';
    return 'internal';
  } catch {
    return 'unknown';
  }
}

function classifyResultPath(path) {
  if (path.includes('/products/')) return 'product';
  if (path.includes('/collections/')) return 'collection';
  if (path.includes('/pages/')) return 'page';
  if (path.includes('/blogs/')) return 'article';
  if (path.includes('/search')) return 'query';
  return 'unknown';
}

function getMainContent() {
  return document.getElementById('MainContent');
}

function getPageType() {
  const template = safeString(getMainContent()?.dataset.template);
  const baseTemplate = template.split('.')[0].split('/')[0];
  if (baseTemplate) return baseTemplate;
  if (window.location.pathname === '/') return 'index';
  return window.location.pathname.split('/').filter(Boolean)[0] || 'unknown';
}

function isProductPage() {
  return getPageType() === 'product';
}

function isProductPageEvent(event) {
  if (!isProductPage()) return false;
  const main = getMainContent();
  const target = event.target instanceof Element ? event.target : null;
  return !target || !main || main.contains(target) || target === document;
}

function getProductId() {
  return safeString(
    document.querySelector('variant-picker[data-product-id]')?.dataset.productId
      || document.querySelector('[data-current-variant-id]')?.dataset.productId
      || document.querySelector('[data-product-id]')?.dataset.productId,
  );
}

function getProductHandle() {
  return getHandleFromHref(window.location.href, 'products');
}

function getCurrentVariantId() {
  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  const checkedVariant = document.querySelector('variant-picker input:checked[data-variant-id]')?.dataset.variantId;
  const currentVariant = document.querySelector('[data-current-variant-id]')?.dataset.currentVariantId;
  const productFormVariant = document.querySelector('input[name="id"]')?.value;

  return safeString(urlVariant || checkedVariant || currentVariant || productFormVariant);
}

function getOptionName(input) {
  if (!input) return '';

  const fieldset = input.closest('fieldset');
  const legend = fieldset?.querySelector('legend');
  const legendText = legend ? legend.cloneNode(true) : null;

  legendText?.querySelectorAll?.('.visually-hidden, [data-selected-option-text], .variant-option__swatch-value').forEach((node) => {
    node.remove();
  });

  return sanitizeLabel(legendText?.textContent || input.name);
}

function getClosestLink(target) {
  return target.closest?.('a[href]') instanceof HTMLAnchorElement ? target.closest('a[href]') : null;
}

function getHandleFromHref(href, resourceType) {
  try {
    const url = new URL(href, window.location.origin);
    const pathParts = url.pathname.split('/').filter(Boolean);
    const resourceIndex = pathParts.indexOf(resourceType);
    return resourceIndex >= 0 ? safeString(pathParts[resourceIndex + 1]) : '';
  } catch {
    return '';
  }
}

function getPathFromHref(href) {
  if (!href) return '';
  try {
    const url = new URL(href, window.location.origin);
    return `${url.pathname}${url.search ? '?...' : ''}${url.hash ? '#...' : ''}`;
  } catch {
    return '';
  }
}

function getSurface(element) {
  if (!element) return 'unknown';
  if (element.closest?.('#header-group, header-component, header-actions')) return 'header';
  if (element.closest?.('footer')) return 'footer';
  if (element.closest?.('.cart-drawer')) return 'cart_drawer';
  if (element.closest?.('.cart-page')) return 'cart_page';
  if (element.closest?.('.predictive-search, #search-modal')) return 'search_modal';
  if (element.closest?.('.facets, facets-form-component')) return 'filters';
  if (element.closest?.('product-card, .product-grid-container, results-list')) return 'product_grid';
  if (element.closest?.('.contact-form')) return 'contact_form';
  return getPageType();
}

function getListContext(element) {
  if (getPageType() === 'collection') return 'collection';
  if (getPageType() === 'search') return 'search';
  if (element.closest?.('[data-testid*="predictive"], .predictive-search')) return 'predictive_search';
  if (element.closest?.('[data-section-type="featured-collection"], .featured-collection')) return 'featured_collection';
  return getSurface(element);
}

function getCardIndex(element) {
  const item = element.closest?.('[ref="cards[]"], .product-grid__item');
  const indexFromClass = Array.from(item?.classList || []).find((className) => className.startsWith('product-grid__item--'));
  if (indexFromClass) return Number(indexFromClass.replace('product-grid__item--', '')) + 1;

  const listItem = element.closest?.('li');
  if (!listItem?.parentElement) return null;
  return Array.from(listItem.parentElement.children).indexOf(listItem) + 1;
}

function getSearchResultPosition(link) {
  const links = Array.from(document.querySelectorAll('.predictive-search a[href]'));
  const index = links.indexOf(link);
  return index >= 0 ? index + 1 : null;
}

function getCartItemCount() {
  const bubble = document.querySelector('[data-testid="cart-bubble"], .cart-bubble, [ref="cartItemCount"]');
  const value = bubble?.textContent?.match(/\d+/)?.[0];
  return value ? Number(value) : null;
}

function getCartLocation(element) {
  if (element?.closest?.('.cart-drawer')) return 'drawer';
  if (element?.closest?.('.cart-page') || getPageType() === 'cart') return 'cart_page';
  return 'unknown';
}

function countActiveFilters() {
  return countFilterParams(new URLSearchParams(window.location.search));
}

function countFilterParams(params) {
  if (!params) return 0;
  return Array.from(params.keys()).filter((key) => key.startsWith('filter.')).length;
}

function safeFilterName(name) {
  return safeString(name).replace(/^filter\.[vpma]\./, '').slice(0, LABEL_MAX_LENGTH);
}

function getViewportCategory() {
  if (window.innerWidth < 750) return 'mobile';
  if (window.innerWidth < 990) return 'tablet';
  return 'desktop';
}

function sanitizeLabel(value) {
  return safeString(value).replace(/\s+/g, ' ').trim().slice(0, LABEL_MAX_LENGTH);
}

function safeString(value) {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function parseBoolean(value) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

function classifyCartError(data) {
  const message = safeString(data?.message || data?.description);
  if (!message) return 'unknown';
  if (/sold|available|stock|inventory/i.test(message)) return 'availability';
  if (/quantity|minimum|maximum/i.test(message)) return 'quantity';
  return 'cart_rejection';
}

function isTrustLabel(label) {
  if (!label) return false;

  return /size|guide|material|gold|diamond|stone|shipping|delivery|return|refund|care|pflege|größe|groesse|material|gold|stein|versand|lieferung|retoure|rückgabe|rueckgabe|garantie/i.test(label);
}

function isFormControlButton(element) {
  return element instanceof HTMLButtonElement && ['submit', 'reset'].includes(element.type);
}

function pruneEmptyValues(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== '' && value !== null && value !== undefined),
  );
}

function throttle(callback, limitMs) {
  let waiting = false;

  return (...args) => {
    if (waiting) return;
    waiting = true;
    callback(...args);
    window.setTimeout(() => {
      waiting = false;
    }, limitMs);
  };
}

function debug(status, eventName, payload) {
  if (!debugEnabled) return;
  console.info('[Melden analytics]', status, eventName, payload);
}
